/**
 * Value ranges step of the cycle configure wizard.
 *
 * A range belongs to one track's criterion: the `range_value` on that track's
 * Criteria & Scales row (`cycle_attribute_configs`, whose track_id is NOT NULL).
 * For each criterion the tracks below use, this step sets one range on every
 * ticked track's row, through `saveTrackRange` — the save the track's Value Ranges
 * tab makes. It is accepted at once on a track where the person may approve it,
 * and proposed where they may only define it. A track whose ranges are locked is
 * listed as locked and left alone.
 */

import { useState, useEffect, useMemo } from 'react';
import { ListChecks, Loader2 } from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { isObjectiveCriterionKey } from '../../../lib/objectiveCriteria';
import {
  useAttributeGovernanceStore,
  criteriaRowsForTrack,
  type CycleAttributeConfig,
  type ValueRange,
} from '../../../stores/prioritization/attributeGovernanceStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { CapabilityId } from '../../../types/prioritization/capabilityIds';
import { wizardTrackTarget } from '../../../lib/cycleWizardTargets';
import { formatRangeDisplay } from '../../../lib/valueRanges';
import {
  CycleTrackTargets,
  TrackWriteReport,
  type TrackWriteFailure,
} from './CycleTrackTargets';
import { useChosenTracks, useCycleOpenTracks } from '../../../hooks/prioritization/useCycleOpenTracks';

// ============================================================================
// Types
// ============================================================================

export interface AttributeRangePanelProps {
  cycleId: string;
  userId: string;
}

// ============================================================================
// Ranges offered: the shapes the track's Value Ranges tab saves and shows
// ============================================================================

type RangeChoice = 'scale5' | 'scale10' | 'fibonacci' | 'hml' | 'tshirt' | 'numbers' | 'list';

const RANGE_PRESETS: Record<Exclude<RangeChoice, 'numbers' | 'list'>, { label: string; range: ValueRange }> = {
  scale5: { label: '1 to 5', range: { type: 'number', min: 1, max: 5, step: 1 } },
  scale10: { label: '1 to 10', range: { type: 'number', min: 1, max: 10, step: 1 } },
  fibonacci: { label: 'Fibonacci', range: { type: 'fibonacci', options: [1, 2, 3, 5, 8, 13] } },
  hml: { label: 'Low / Medium / High', range: { type: 'select', options: ['Low', 'Medium', 'High'] } },
  tshirt: { label: 'T-shirt sizes', range: { type: 'select', options: ['XS', 'S', 'M', 'L', 'XL'] } },
};

const CHOICE_LABELS: Record<RangeChoice, string> = {
  ...Object.fromEntries(Object.entries(RANGE_PRESETS).map(([k, v]) => [k, v.label])) as Record<keyof typeof RANGE_PRESETS, string>,
  numbers: 'Numbers from … to …',
  list: 'Your own list',
};

function rangeFor(choice: RangeChoice, min: number, max: number, listText: string): ValueRange | null {
  if (choice === 'numbers') return max > min ? { type: 'number', min, max, step: 1 } : null;
  if (choice === 'list') {
    const options = listText.split(',').map(s => s.trim()).filter(Boolean);
    return options.length >= 2 ? { type: 'custom', options } : null;
  }
  return RANGE_PRESETS[choice].range;
}

const sameRange = (a: ValueRange | undefined, b: ValueRange | undefined) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// ============================================================================
// One criterion
// ============================================================================

function CriterionRangeCard({
  label,
  rows,
  tracksWithout,
  trackName,
  mayDefine,
  saving,
  onSave,
}: {
  label: string;
  /** The criterion's row on each ticked track that uses it. */
  rows: CycleAttributeConfig[];
  /** Ticked tracks that do not use this criterion. */
  tracksWithout: string[];
  trackName: (trackId: string) => string;
  /** The person may set this range on at least one of those tracks. */
  mayDefine: boolean;
  saving: boolean;
  onSave: (range: ValueRange) => void;
}) {
  const [choice, setChoice] = useState<RangeChoice>('scale5');
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(10);
  const [listText, setListText] = useState('');
  const range = rangeFor(choice, min, max, listText);

  const allSame = rows.every(r => sameRange(r.rangeValue, rows[0]?.rangeValue));

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-3" data-testid={`range-card-${rows[0]?.attributeKey}`}>
      <div>
        <h4 className="font-medium text-[var(--text-pri)]">{label}</h4>
        {allSame ? (
          <p className="text-sm text-[var(--text-sec)]">
            Now: {rows[0]?.rangeValue ? formatRangeDisplay(rows[0].rangeValue) : 'no range'}
            {rows[0]?.rangeValue ? ` (${rows.filter(r => r.rangeStatus === 'accepted').length} of ${rows.length} accepted)` : ''}
          </p>
        ) : (
          <ul className="text-sm text-[var(--text-sec)]">
            {rows.map(r => (
              <li key={r.id}>
                {trackName(r.trackId)}: {r.rangeValue ? `${formatRangeDisplay(r.rangeValue)} (${r.rangeStatus})` : 'no range'}
              </li>
            ))}
          </ul>
        )}
        {tracksWithout.length > 0 && (
          <p className="text-xs text-[var(--text-ter)]">Not a criterion on {tracksWithout.map(trackName).join(', ')}.</p>
        )}
      </div>

      {mayDefine ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Range for ${label}`}>
            {(Object.keys(CHOICE_LABELS) as RangeChoice[]).map(c => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={choice === c}
                onClick={() => setChoice(c)}
                className={`px-3 py-1.5 border text-sm ${
                  choice === c ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-sec)]'
                }`}
              >
                {CHOICE_LABELS[c]}
              </button>
            ))}
          </div>
          {choice === 'numbers' && (
            <div className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-1">
                From
                <input type="number" value={min} onChange={e => setMin(Number(e.target.value))} className="w-20 px-2 py-1 border border-[var(--rule)]" />
              </label>
              <label className="flex items-center gap-1">
                to
                <input type="number" value={max} onChange={e => setMax(Number(e.target.value))} className="w-20 px-2 py-1 border border-[var(--rule)]" />
              </label>
            </div>
          )}
          {choice === 'list' && (
            <label className="block text-sm">
              Values, lowest first, separated by commas
              <input
                type="text"
                value={listText}
                onChange={e => setListText(e.target.value)}
                placeholder="Small, Medium, Large"
                className="mt-1 w-full px-2 py-1 border border-[var(--rule)]"
              />
            </label>
          )}
          <div className="flex items-center justify-end gap-3">
            {!range && <span className="text-xs text-[var(--mustard)]">Give two or more values, lowest first.</span>}
            <button
              type="button"
              disabled={!range || saving}
              onClick={() => range && onSave(range)}
              className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Set this range on {rows.length} track{rows.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--text-ter)]">
          Setting this range takes the right to define value ranges for {label}.
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AttributeRangePanel({ cycleId, userId }: AttributeRangePanelProps) {
  const attributes = useAttributesStore(state => state.attributes);
  const fetchAttributes = useAttributesStore(state => state.fetchFromSupabase);
  const cycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);
  const fetchCycleConfigs = useAttributeGovernanceStore(state => state.fetchCycleConfigs);
  const saveTrackRange = useAttributeGovernanceStore(state => state.saveTrackRange);
  const canDefineRange = useAttributeGovernanceStore(state => state.canDefineRange);
  const canApproveRange = useAttributeGovernanceStore(state => state.canApproveRange);
  // The capability set the track's Value Ranges tab reads (DB-resolved).
  const userCapabilities = (useUserStore(state => state.resolvedCapabilityIds) ?? []) as CapabilityId[];

  useEffect(() => {
    void fetchAttributes();
    void fetchCycleConfigs(cycleId);
  }, [cycleId, fetchAttributes, fetchCycleConfigs]);

  const { tracks } = useCycleOpenTracks(cycleId);
  const targets = tracks.map(t => wizardTrackTarget('ranges', t));
  const { chosenIds, toggle: toggleTrack } = useChosenTracks(targets);

  const trackName = (id: string) => tracks.find(t => t.id === id)?.name ?? 'A track';
  const labelOf = (key: string) => attributes.find(a => a.key === key)?.label ?? key;

  // The criteria the ticked tracks use, each with its row on each of those tracks.
  const byCriterion = useMemo(() => {
    const map = new Map<string, CycleAttributeConfig[]>();
    for (const t of tracks) {
      if (!chosenIds.includes(t.id)) continue;
      for (const row of criteriaRowsForTrack(cycleConfigs, t)) {
        map.set(row.attributeKey, [...(map.get(row.attributeKey) ?? []), row]);
      }
    }
    return map;
  }, [tracks, chosenIds, cycleConfigs]);

  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [report, setReport] = useState<{ saved: number; failures: TrackWriteFailure[] } | null>(null);

  const handleSave = async (key: string, range: ValueRange) => {
    setSavingKey(key);
    const failures: TrackWriteFailure[] = [];
    let saved = 0;
    try {
      for (const row of byCriterion.get(key) ?? []) {
        const what = `the ${labelOf(key)} range`;
        if (!canDefineRange(key, userCapabilities, cycleId, row.trackId)) {
          failures.push({ trackName: trackName(row.trackId), what, error: 'you may not define this range here' });
          continue;
        }
        const accept = canApproveRange(key, userCapabilities, cycleId, row.trackId);
        const { error } = await saveTrackRange(row.id, range, { by: userId, accept });
        if (error) failures.push({ trackName: trackName(row.trackId), what, error });
        else saved += 1;
      }
      await fetchCycleConfigs(cycleId);
      setReport({ saved, failures });
    } finally {
      setSavingKey(null);
    }
  };

  // A strategic objective (1066) is scored on the track's range and takes none of its own.
  const keys = [...byCriterion.keys()].filter(k => !isObjectiveCriterionKey(k)).sort((a, b) => labelOf(a).localeCompare(labelOf(b)));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)] flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-[var(--text-ter)]" />
          Value ranges
        </h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Say what values each criterion takes. A range is set on each track below, as on a
          track&apos;s Value ranges tab: accepted where you may approve ranges, proposed where you
          may only define them.
        </p>
      </div>

      <CycleTrackTargets targets={targets} chosenIds={chosenIds} onToggle={toggleTrack} />

      {report && <TrackWriteReport saved={report.saved} failures={report.failures} />}

      {keys.length === 0 ? (
        <p className="text-sm text-[var(--text-sec)]">
          {chosenIds.length === 0 ? 'No track here can take a range.' : 'These tracks have no criteria yet. Pick criteria in the step before.'}
        </p>
      ) : (
        <div className="space-y-3">
          {keys.map(key => {
            const rows = byCriterion.get(key) ?? [];
            return (
              <CriterionRangeCard
                key={key}
                label={labelOf(key)}
                rows={rows}
                tracksWithout={chosenIds.filter(id => !rows.some(r => r.trackId === id))}
                trackName={trackName}
                mayDefine={rows.some(r => canDefineRange(key, userCapabilities, cycleId, r.trackId))}
                saving={savingKey === key}
                onSave={range => void handleSave(key, range)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AttributeRangePanel;
